import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Gift, Loader2, Copy, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { rewards as api } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";

const empty = { reward_type: "cashback", reward_value: 10000, min_order: 50000, max_uses: 1, expiry_date: "" };

export default function Rewards() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const fetchList = async () => {
    setLoading(true);
    try { setRows(await api.list()); } catch (err) { console.warn("Rewards load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchList(); }, []);

  const create = async () => {
    try {
      await api.create(form);
      toast.success("Reward dibuat");
      setOpen(false); setForm(empty); fetchList();
    } catch { toast.error("Gagal"); }
  };

  const markUsed = async (id) => {
    await api.use(id); toast.success("Reward ditandai terpakai"); fetchList();
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Kode disalin"); };

  const active = rows.filter(r => r.status === "active").length;
  const used = rows.filter(r => r.status === "used").length;
  const totalValue = rows.filter(r => r.status === "used").reduce((s, r) => s + r.reward_value, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Cashback & Reward</h1>
          <p className="text-sm text-gray-500 mt-1">Kelola semua voucher, cashback, dan poin reward Anda.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-reward-btn"><Plus className="mr-2 h-4 w-4" /> Buat Reward</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Buat Reward Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Tipe</Label>
                <Select value={form.reward_type} onValueChange={(v) => setForm({ ...form, reward_type: v })}>
                  <SelectTrigger data-testid="reward-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cashback">Cashback</SelectItem>
                    <SelectItem value="voucher">Voucher</SelectItem>
                    <SelectItem value="points">Poin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nilai</Label><Input type="number" value={form.reward_value} onChange={(e) => setForm({ ...form, reward_value: Number(e.target.value) })} data-testid="reward-value" /></div>
                <div><Label>Min. Order</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: Number(e.target.value) })} data-testid="reward-min" /></div>
                <div><Label>Max Uses</Label><Input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Number(e.target.value) })} data-testid="reward-max" /></div>
                <div><Label>Expiry</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="reward-expiry" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-reward-btn">Buat</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-gray-200"><CardContent className="p-5"><div className="text-xs text-gray-500">Reward Aktif</div><div className="mt-1 font-heading text-2xl font-bold text-emerald-600">{active}</div></CardContent></Card>
        <Card className="border-gray-200"><CardContent className="p-5"><div className="text-xs text-gray-500">Sudah Terpakai</div><div className="mt-1 font-heading text-2xl font-bold text-gray-900">{used}</div></CardContent></Card>
        <Card className="border-gray-200"><CardContent className="p-5"><div className="text-xs text-gray-500">Total Value Terpakai</div><div className="mt-1 font-heading text-2xl font-bold text-amber-600">{formatIDR(totalValue)}</div></CardContent></Card>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Gift className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada reward</h3>
              <p className="mt-1 text-sm text-gray-500">Reward otomatis dibuat saat customer scan QR loyalty.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Tipe</th>
                    <th className="px-4 py-3">Nilai</th>
                    <th className="px-4 py-3">Min. Order</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Dibuat</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button onClick={() => copy(r.reward_code)} className="font-mono font-semibold text-emerald-700 hover:underline flex items-center gap-1" data-testid={`copy-${r.id}`}>
                          {r.reward_code} <Copy size={12} />
                        </button>
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-700">{r.reward_type}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{formatIDR(r.reward_value)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatIDR(r.min_order)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`border text-[10px] ${r.status === "used" ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {r.status === "used" ? "Terpakai" : "Aktif"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        {r.status === "active" && (
                          <Button size="sm" variant="outline" onClick={() => markUsed(r.id)} data-testid={`use-${r.id}`}><Check size={12} className="mr-1" /> Tandai Terpakai</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
